import {
  DetailledUser,
  MyPurchaseHistory,
  MyUserType,
  Paginated,
  PaginatedResponse,
  QueryArgs,
  Sortable,
  UserShortType,
  UserType,
  UserWithStructureType,
} from "@ac-expo/api-types";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import userQueryKeys, { UsersFilter } from "../query-keys/user.query-keys";

export const getMyUser = async () => {
  const { data } = await privateAxios.get<MyUserType>("/users/me");
  return data;
};

export const useMyUserQuery = (options?: QueryArgs<MyUserType>) => {
  return useQuery({
    queryKey: userQueryKeys.me(),
    queryFn: getMyUser,
    ...options,
  });
};

const getUserByConnectId = async (connectId: string) => {
  const res = await privateAxios.get<UserType>(`/users/connect/${connectId}`);
  return res.data;
};

export const useGetUserByConnectIdQuery = (connectId: string) => {
  return useQuery({
    queryKey: userQueryKeys.connect(connectId),
    queryFn: () => getUserByConnectId(connectId),
    enabled: !!connectId && connectId.length === 10,
  });
};

export const getUserByIdQuery = async (id: string) => {
  const res = await privateAxios.get<UserType>(`/users/id/${id}`);
  return res.data;
};

export const useGetUserByIdQuery = (id: string | undefined) => {
  return useQuery({
    enabled: !!id,
    queryKey: userQueryKeys.byId(id!),
    queryFn: () => getUserByIdQuery(id!),
  });
};

const getUsersList = async (filters?: Sortable<Paginated<UsersFilter>>) => {
  try {
    const res = await privateAxios.get<PaginatedResponse<UserWithStructureType>>(`/users`, {
      params: filters,
    });
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useGetUsersQuery = (
  filters?: Sortable<Paginated<UsersFilter>>,
  options?: QueryArgs<PaginatedResponse<UserWithStructureType>>
) => {
  return useQuery({
    queryKey: userQueryKeys.list(filters),
    queryFn: () => getUsersList(filters),
    ...options,
  });
};

const getUser = async (id: string) => {
  try {
    const { data } = await privateAxios.get<DetailledUser>(`/users/${id}`);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useUserQuery = (id: string) => {
  return useQuery({
    queryKey: userQueryKeys.detail(id),
    queryFn: () => getUser(id),
    enabled: !!id,
  });
};

const getAcExpoUsers = async () => {
  try {
    const { data } = await privateAxios.get<UserShortType[]>("/users/acExpo");
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useAcExpoUsersQuery = () => {
  return useQuery({
    queryKey: userQueryKeys.acExpo(),
    queryFn: getAcExpoUsers,
  });
};

const getMyPurchasesHistory = async () => {
  const { data } = await privateAxios.get<MyPurchaseHistory[]>(
    "/users/purchases-history/me"
  );
  return data;
};

export const useMyPurchasesHistoryQuery = () => {
  return useQuery({
    queryKey: userQueryKeys.purchasesHistory(),
    queryFn: getMyPurchasesHistory,
  });
};
